// Threat Insights — list panel rendered when the Findings page mode is
// "threats". Keeps the surgery in `findings/index.js` tiny: the parent
// owns the page header + mode toggle; this component owns everything
// below it.
//
// Hits the Phase-1 CP route `GET /api/user/v1/threat-insights/findings`
// scoped to the global site selector (when one site is active). When
// the operator has the All Sites selector on, we omit `site_id` and
// rely on the CP's tenant-scoped default.
//
// Filter pills (severity, status, rule, time range) mirror the posture
// pill pattern so the operator's muscle memory transfers. Search is
// intentionally disabled here — the Phase-1 CP doesn't accept `q`.

import React from "react";
import $ from "jquery";
import {
  Box,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TablePagination,
  Select,
  MenuItem,
  Button,
  Chip,
  Tooltip,
  Skeleton,
  IconButton,
} from "@mui/material";
import ShieldOutlinedIcon from "@mui/icons-material/ShieldOutlined";
import VolumeOffOutlinedIcon from "@mui/icons-material/VolumeOffOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import KeyOutlinedIcon from "@mui/icons-material/KeyOutlined";
import CookieOutlinedIcon from "@mui/icons-material/CookieOutlined";
import DnsOutlinedIcon from "@mui/icons-material/DnsOutlined";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import HelpOutlineOutlinedIcon from "@mui/icons-material/HelpOutlineOutlined";
import { Filter } from "lucide-react";

import colors from "../../../../utils/colors";
import { formatDate } from "../../../../utils/format";
import threatInsights from "../../../../utils/axios/v1/threatInsightsAxios";
import { DETECTION_ICONS } from "../../../../icons/detectionLucideIcons";
import useGlobalSite from "../../../../hooks/user/useGlobalSite";
import RefreshIconButton from "../../../common/RefreshIconButton";
import LayerdIcon from "../../../common/LayerdIcon";
import { GreenTimeRangePicker } from "../../../common/Greens";
import { tableHeadSx, tableBodySx, getSeverityConfig } from "../detection/WAFEvent";
import ThreatInsightDrawer from "./ThreatInsightDrawer";
import { useNotification } from "../../../../contexts/NotificationContext";

const brandBlue = colors.primary[300];
const FILTER_CONTROL_HEIGHT = 36;

const EVENT_FILTER_BAR_SX = {
  display: "flex",
  flexDirection: "column",
  gap: 2,
  p: 2,
  backgroundColor: "white",
  borderRadius: "8px",
  border: `1px solid ${colors.neutral[200]}`,
};

const filterOutlinedRootSx = {
  height: FILTER_CONTROL_HEIGHT,
  minHeight: FILTER_CONTROL_HEIGHT,
  maxHeight: FILTER_CONTROL_HEIGHT,
  borderRadius: "8px",
  fontSize: 14,
  backgroundColor: "#fff",
  boxSizing: "border-box",
  "& fieldset": { borderColor: colors.neutral[200] },
  "&:hover fieldset": { borderColor: colors.neutral[300] },
  "&.Mui-focused fieldset": { borderColor: brandBlue },
};

const filterSelectSx = {
  height: FILTER_CONTROL_HEIGHT,
  "& .MuiOutlinedInput-root": filterOutlinedRootSx,
  "& .MuiSelect-select": {
    py: 0,
    pl: 1.5,
    pr: 4,
    height: `${FILTER_CONTROL_HEIGHT}px !important`,
    minHeight: `${FILTER_CONTROL_HEIGHT}px !important`,
    lineHeight: "20px",
    display: "flex",
    alignItems: "center",
    boxSizing: "border-box",
  },
  "& .MuiSelect-icon": {
    top: "50%",
    transform: "translateY(-50%)",
    right: 8,
  },
};

function getTimeBounds(timeRange, customDateRange) {
  if (timeRange === "custom" && customDateRange?.length === 2) {
    return {
      from: new Date(customDateRange[0]).toISOString(),
      to: new Date(customDateRange[1]).toISOString(),
    };
  }
  const to = new Date();
  const from = new Date(to);
  switch (timeRange) {
    case "30m":
      from.setMinutes(from.getMinutes() - 30);
      break;
    case "60m":
      from.setMinutes(from.getMinutes() - 60);
      break;
    case "3h":
      from.setHours(from.getHours() - 3);
      break;
    case "6h":
      from.setHours(from.getHours() - 6);
      break;
    case "24h":
      from.setHours(from.getHours() - 24);
      break;
    case "3d":
      from.setDate(from.getDate() - 3);
      break;
    case "7d":
      from.setDate(from.getDate() - 7);
      break;
    default:
      from.setDate(from.getDate() - 7);
  }
  return { from: from.toISOString(), to: to.toISOString() };
}

function getThreatStatusLabel(status) {
  switch ((status || "").toLowerCase()) {
    case "open":
      return "Open";
    case "acknowledged":
      return "Acknowledged";
    case "resolved":
      return "Resolved";
    case "suppressed":
      return "Suppressed";
    default:
      return status || "—";
  }
}

// TI-3 (2026-07-02) — icon + label per subject_kind, so the operator
// can't confuse an identity finding for an IP finding at a glance.
const SUBJECT_KIND_META = {
  ip:       { Icon: DnsOutlinedIcon,          label: "IP",            color: "#1E40AF" },
  identity: { Icon: PersonOutlineOutlinedIcon, label: "Identity",      color: "#6B3FA0" },
  apikey:   { Icon: KeyOutlinedIcon,          label: "API key",       color: "#B25A1A" },
  session:  { Icon: CookieOutlinedIcon,       label: "Session",       color: "#15707E" },
  host:     { Icon: LanguageOutlinedIcon,     label: "Host",          color: "#0f7a42" },
  global:   { Icon: PublicOutlinedIcon,       label: "Cross-site",    color: "#A11228" },
  unknown:  { Icon: HelpOutlineOutlinedIcon,  label: "Unknown",       color: "#7B7B7B" },
};

function SubjectChip({ kind, value }) {
  const meta = SUBJECT_KIND_META[kind] || SUBJECT_KIND_META.unknown;
  const Icon = meta.Icon;
  return (
    <Stack direction="row" alignItems="center" spacing={0.75}>
      <Box
        sx={{
          width: 24, height: 24, borderRadius: "6px",
          background: `${meta.color}15`,
          color: meta.color,
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Icon sx={{ fontSize: 14 }} />
      </Box>
      <Stack sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontSize: 11, lineHeight: "13px", fontWeight: 600,
            color: meta.color, textTransform: "uppercase", letterSpacing: "0.03em",
          }}
        >
          {meta.label}
        </Typography>
        {value ? (
          <Typography
            sx={{
              fontSize: 12.5, lineHeight: "16px",
              fontFamily: kind === "ip" || kind === "apikey" ? "monospace" : undefined,
              color: colors.neutral[900],
              maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            }}
            title={value}
          >
            {value}
          </Typography>
        ) : (
          <Typography sx={{ fontSize: 12, color: colors.neutral[500] }}>—</Typography>
        )}
      </Stack>
    </Stack>
  );
}

function getThreatStatusColor(status) {
  switch ((status || "").toLowerCase()) {
    case "open":
      return colors.red[100];
    case "acknowledged":
      return colors.yellow[100];
    case "resolved":
      return colors.green[300];
    case "suppressed":
      return colors.neutral[400];
    default:
      return colors.neutral[800];
  }
}

// ─── Skeleton ────────────────────────────────────────────────────────
function SkeletonRows({ rows }) {
  const columnCount = 8;
  return (
    <TableBody>
      {Array.from({ length: rows }).map((_, r) => (
        <TableRow key={r}>
          {Array.from({ length: columnCount }).map((__, c) => (
            <TableCell key={c} sx={{ padding: "16px 8px" }}>
              <Skeleton variant="rectangular" height={22} sx={{ borderRadius: "11px" }} />
            </TableCell>
          ))}
          <TableCell sx={{ padding: "16px 8px", width: 48 }}>
            <Skeleton variant="circular" width={18} height={18} />
          </TableCell>
        </TableRow>
      ))}
    </TableBody>
  );
}

// ─── Constants ───────────────────────────────────────────────────────
const SEVERITY_OPTIONS = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "critical", label: "Critical" },
];

const STATUS_OPTIONS = [
  { value: "open", label: "Open" },
  { value: "acknowledged", label: "Acknowledged" },
  { value: "resolved", label: "Resolved" },
  { value: "suppressed", label: "Suppressed" },
];

const DEFAULT_LIMIT = 50;

// ─── Panel ───────────────────────────────────────────────────────────
export default function ThreatInsightsPanel() {
  const { effectiveSiteId, isAllSitesSelected } = useGlobalSite();
  const { showSuccess, showError } = useNotification();

  // Filter state — `status` defaults to "open" per the Phase 1 contract.
  const [severity, setSeverity] = React.useState(null);
  const [status, setStatus] = React.useState("open");
  const [ruleId, setRuleId] = React.useState("");
  // TI-11 (2026-07-03) — Per-site vs Cross-site tabs. Determines the
  // client-side filter on rule.scope so global findings show under
  // their own tab rather than mingled with per-site rows. Renamed
  // from "Cross-tenant" 2026-07-04 — the underlying scan drops the
  // per-site host predicate, so the axis is "site", not "tenant".
  const [scopeTab, setScopeTab] = React.useState("per_site");
  const [timeRange, setTimeRange] = React.useState("7d");
  const [customDateRange, setCustomDateRange] = React.useState([
    "2000-01-01T00:00:00",
    "2000-01-02T00:00:00",
  ]);
  const [page, setPage] = React.useState(0);

  // LE-290: open drawer directly in silence form mode (replaces TI-5 dialog).
  const [drawerSilenceMode, setDrawerSilenceMode] = React.useState(false);

  React.useEffect(() => {
    const now = new Date();
    const oneDayAgo = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const date = String(now.getDate()).padStart(2, "0");
    const year = String(now.getFullYear());
    const _month = String(oneDayAgo.getMonth() + 1).padStart(2, "0");
    const _date = String(oneDayAgo.getDate()).padStart(2, "0");
    const _year = String(oneDayAgo.getFullYear());
    setCustomDateRange([
      `${_year}-${_month}-${_date}T00:00:00`,
      `${year}-${month}-${date}T00:00:00`,
    ]);
  }, []);

  const selectPeriod = (period) => {
    setTimeRange(period);
    if (period === "custom") {
      $(document).ready(() => {
        $(".react-minimal-datetime-range__range-input-wrapper").click();
        $(".react-minimal-datetime-range").css({
          "margin-top": $(".react-minimal-datetime-range__range-input-wrapper").parent().parent().parent().height() + 4,
        });
      });
    }
  };

  const selectCustomDateRange = (res) => {
    setCustomDateRange(res);
  };

  // Data state.
  const [items, setItems] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [loading, setLoading] = React.useState(false);
  const [errMsg, setErrMsg] = React.useState(null);
  const [refreshing, setRefreshing] = React.useState(false);
  // Freshness of the last cross-site (global-scope) finding — surfaced
  // next to the Cross-site tab so an empty tab doesn't silently mask
  // a scanner regression. Populated from body.meta.global_freshness on
  // every fetch; `null` on empty response.
  const [globalFreshness, setGlobalFreshness] = React.useState(null);

  // Drawer state.
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [selectedFinding, setSelectedFinding] = React.useState(null);

  // Rule list seeded from whatever findings the operator has loaded.
  // The CP can't enumerate rules at the tenant surface (Phase 1
  // catalog routes are admin-only), so this is a best-effort derived
  // list — empty until at least one fetch returns. Hard-cap at 20 to
  // stay friendly with the menu.
  const knownRules = React.useMemo(() => {
    if (!Array.isArray(items)) return [];
    const m = new Map();
    items.forEach((f) => {
      const id = f?.rule_id || f?.rule?.id;
      const name = f?.rule?.name || id;
      if (id && !m.has(id)) m.set(id, name);
    });
    return Array.from(m.entries()).slice(0, 20);
  }, [items]);

  // ─── Fetch ───────────────────────────────────────────────────────
  const buildParams = React.useCallback(() => {
    const params = { page, limit: DEFAULT_LIMIT };
    if (status) params.status = status;
    if (severity) params.severity = severity;
    if (ruleId) params.rule_id = ruleId;
    if (effectiveSiteId && !isAllSitesSelected) params.site_id = effectiveSiteId;
    const bounds = getTimeBounds(timeRange, customDateRange);
    params.from = bounds.from;
    params.to = bounds.to;
    return params;
  }, [page, status, severity, ruleId, effectiveSiteId, isAllSitesSelected, timeRange, customDateRange]);

  const fetchFindings = React.useCallback(async () => {
    setLoading(true);
    setErrMsg(null);
    try {
      const res = await threatInsights.findings(buildParams());
      const body = res?.data || {};
      const list = Array.isArray(body.items) ? body.items : [];
      setItems(list);
      setTotal(Number(body.total || list.length));
      setGlobalFreshness(body?.meta?.global_freshness || null);
    } catch (err) {
      const msg =
        (err && err.response && err.response.data && err.response.data.message) ||
        (typeof err === "string" ? err : null) ||
        "Failed to load threat insights";
      setErrMsg(msg);
      setItems([]);
      setTotal(0);
      setGlobalFreshness(null);
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  React.useEffect(() => {
    fetchFindings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchFindings]);

  // Reset to page 0 whenever a filter changes — same UX as the posture
  // page (filter change → top of list).
  React.useEffect(() => {
    setPage(0);
  }, [severity, status, ruleId, timeRange, customDateRange, effectiveSiteId, isAllSitesSelected]);

  const refresh = async () => {
    setRefreshing(true);
    try { await fetchFindings(); } finally { setRefreshing(false); }
  };

  const hasActiveFilters = Boolean(
    severity || status !== "open" || ruleId || timeRange !== "7d"
  );

  const clearAll = () => {
    setSeverity(null);
    setStatus("open");
    setRuleId("");
    setTimeRange("7d");
    setPage(0);
  };

  // ─── Drawer handlers ─────────────────────────────────────────────
  const openDrawer = (finding, { silenceMode = false } = {}) => {
    setSelectedFinding(finding);
    setDrawerSilenceMode(silenceMode);
    setDrawerOpen(true);
  };
  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelectedFinding(null);
    setDrawerSilenceMode(false);
  };

  // TI-11: partition items into per-site vs global (cross-site).
  const perSiteItems  = React.useMemo(() => items.filter((f) => (f.rule?.scope || "per_site") !== "global"), [items]);
  const globalItems   = React.useMemo(() => items.filter((f) => (f.rule?.scope || "per_site") === "global"), [items]);
  const visibleItems  = scopeTab === "global" ? globalItems : perSiteItems;

  // Format the cross-site freshness for the chip. Hours < 24 → "Xh",
  // above → "Xd". `never` prints as "Never" so the operator knows
  // there's no history rather than a fresh-but-stale state.
  const freshnessChip = React.useMemo(() => {
    if (!globalFreshness) return null;
    const { health, hours_since } = globalFreshness;
    if (health === "never" || hours_since == null) {
      return { label: "Never fired", color: "#7B7B7B", bg: "#F1F1F1" };
    }
    let label;
    if (hours_since < 1) label = "Last: <1h ago";
    else if (hours_since < 24) label = `Last: ${Math.round(hours_since)}h ago`;
    else label = `Last: ${Math.round(hours_since / 24)}d ago`;
    // Health-driven color: fresh (green), aging (amber), stale (red).
    if (health === "fresh") return { label, color: "#0f7a42", bg: "rgba(15,122,66,0.10)" };
    if (health === "aging") return { label, color: "#B25A1A", bg: "rgba(178,90,26,0.10)" };
    return { label, color: "#A11228", bg: "rgba(161,18,40,0.10)" };
  }, [globalFreshness]);

  // ─── Render ──────────────────────────────────────────────────────
  return (
    <>
      <Stack spacing={6}>
        <Stack spacing={2}>
          {/* TI-11: scope tabs above the filter bar. Client-side split
              so we don't need a second server round-trip; totals per
              tab reflect the CURRENT filter set (severity/status/etc.). */}
          <Box sx={{ display: "flex", gap: 1, borderBottom: `1px solid ${colors.neutral[200]}`, pb: 0 }}>
            {[
              { key: "per_site", label: "Per-site",   count: perSiteItems.length },
              { key: "global",   label: "Cross-site", count: globalItems.length  },
            ].map((t) => {
              const on = scopeTab === t.key;
              // Freshness chip only renders on the global tab — that's
              // where an empty count can hide a scanner regression.
              const showFreshness = t.key === "global" && freshnessChip;
              return (
                <Box
                  key={t.key}
                  onClick={() => setScopeTab(t.key)}
                  sx={{
                    cursor: "pointer",
                    px: 2, py: 1.25,
                    borderBottom: on ? `2px solid ${brandBlue}` : "2px solid transparent",
                    marginBottom: "-1px",
                    display: "flex", alignItems: "center", gap: 1,
                  }}
                >
                  <Typography sx={{
                    fontSize: 14,
                    fontWeight: on ? 600 : 500,
                    color: on ? brandBlue : colors.neutral[700],
                  }}>
                    {t.label}
                  </Typography>
                  <Chip
                    size="small"
                    label={t.count}
                    sx={{
                      height: 20,
                      fontSize: 11,
                      fontWeight: 600,
                      background: on ? "rgba(99,113,247,0.10)" : colors.neutral[100],
                      color: on ? brandBlue : colors.neutral[700],
                      border: `1px solid ${on ? "rgba(99,113,247,0.22)" : colors.neutral[200]}`,
                    }}
                  />
                  {showFreshness && (
                    <Tooltip
                      title={
                        globalFreshness?.last_at
                          ? `Most recent cross-site finding: ${new Date(globalFreshness.last_at).toLocaleString()}`
                          : "No cross-site findings have been recorded yet"
                      }
                      arrow
                    >
                      <Chip
                        size="small"
                        label={freshnessChip.label}
                        sx={{
                          height: 20,
                          fontSize: 11,
                          fontWeight: 500,
                          background: freshnessChip.bg,
                          color: freshnessChip.color,
                          border: `1px solid ${freshnessChip.color}22`,
                        }}
                      />
                    </Tooltip>
                  )}
                </Box>
              );
            })}
          </Box>

          <Box sx={EVENT_FILTER_BAR_SX}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ gap: 1.5 }}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={2}
                flexWrap="wrap"
                sx={{ gap: 1.5, flex: 1 }}
              >
                <Stack direction="row" alignItems="center" spacing={1}>
                  <LayerdIcon icon={Filter} size={18} color="primary" />
                  <Typography sx={{ fontSize: 14, fontWeight: 500, color: colors.neutral[800] }}>
                    Filters
                  </Typography>
                </Stack>

                <Select
                  value={severity || ""}
                  displayEmpty
                  onChange={(e) => setSeverity(e.target.value || null)}
                  sx={{ ...filterSelectSx, minWidth: 140 }}
                  renderValue={(v) => (v ? `Severity: ${v}` : "Severity")}
                >
                  <MenuItem value="">All severities</MenuItem>
                  {SEVERITY_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 14 }}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>

                <Select
                  value={status || "open"}
                  onChange={(e) => setStatus(e.target.value)}
                  sx={{ ...filterSelectSx, minWidth: 140 }}
                  renderValue={(v) => {
                    const label = STATUS_OPTIONS.find((o) => o.value === v)?.label || v;
                    return `Status: ${label}`;
                  }}
                >
                  <MenuItem value="all" sx={{ fontSize: 14 }}>All statuses</MenuItem>
                  {STATUS_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: 14 }}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>

                <Select
                  value={ruleId}
                  displayEmpty
                  onChange={(e) => setRuleId(e.target.value)}
                  sx={{ ...filterSelectSx, minWidth: 160 }}
                  renderValue={(v) => {
                    if (!v) return "Rule";
                    const name = knownRules.find(([id]) => id === v)?.[1];
                    return name ? `Rule: ${name}` : `Rule: ${v}`;
                  }}
                >
                  <MenuItem value="" sx={{ fontSize: 14 }}>All rules</MenuItem>
                  {knownRules.length === 0 ? (
                    <MenuItem disabled sx={{ fontSize: 14, color: colors.neutral[500] }}>
                      No rules in current view
                    </MenuItem>
                  ) : (
                    knownRules.map(([id, name]) => (
                      <MenuItem key={id} value={id} sx={{ fontSize: 14 }}>
                        {name}
                      </MenuItem>
                    ))
                  )}
                </Select>

                {hasActiveFilters && (
                  <Chip
                    label="Clear All"
                    onClick={clearAll}
                    sx={{
                      height: 36,
                      fontSize: 14,
                      borderRadius: "8px",
                      backgroundColor: "#fff",
                      border: `1px solid ${colors.neutral[200]}`,
                      color: colors.neutral[800],
                      "&:hover": {
                        borderColor: colors.red[100],
                        color: colors.red[100],
                      },
                    }}
                  />
                )}
              </Stack>

              <Stack direction="row" alignItems="center" spacing={2} sx={{ flexShrink: 0 }}>
                <RefreshIconButton onClick={refresh} disabled={refreshing || loading} loading={refreshing} />
                <GreenTimeRangePicker
                  selectPeriod={selectPeriod}
                  selectCustomDateRange={selectCustomDateRange}
                  timeRange={timeRange}
                  customDateRange={customDateRange}
                />
              </Stack>
            </Stack>
          </Box>
        </Stack>

        <Box
          sx={{
            border: `solid 1px ${colors.neutral[200]}`,
            borderRadius: "8px",
            p: 2,
            overflow: "hidden",
          }}
        >
          <Table
            sx={{
              "& .MuiTableCell-head": { py: 3, px: 3 },
              "& .MuiTableCell-body": { py: 2, px: 3 },
              "& .MuiTableCell-root": { borderBottom: "none" },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell>
                  <Typography sx={tableHeadSx}>Site</Typography>
                </TableCell>
                <TableCell>
                  <Typography sx={tableHeadSx}>Rule</Typography>
                </TableCell>
                <TableCell sx={{ width: 260 }}>
                  <Typography sx={tableHeadSx}>Subject</Typography>
                </TableCell>
                <TableCell>
                  <Typography sx={tableHeadSx}>Matches</Typography>
                </TableCell>
                <TableCell>
                  <Typography sx={tableHeadSx}>Type of detection</Typography>
                </TableCell>
                <TableCell>
                  <Typography sx={tableHeadSx}>Severity</Typography>
                </TableCell>
                <TableCell>
                  <Typography sx={tableHeadSx}>Status</Typography>
                </TableCell>
                <TableCell sx={{ width: 230 }}>
                  <Typography sx={tableHeadSx}>Last seen</Typography>
                </TableCell>
                <TableCell sx={{ width: 130 }}>
                  <Typography sx={tableHeadSx}>Finding ID</Typography>
                </TableCell>
                <TableCell sx={{ width: 72 }}>
                  <Typography sx={tableHeadSx}>Action</Typography>
                </TableCell>
              </TableRow>
            </TableHead>

            {loading && visibleItems.length === 0 ? (
              <SkeletonRows rows={10} />
            ) : errMsg ? (
              <TableBody>
                <TableRow>
                  <TableCell colSpan={10}>
                    <Stack alignItems="center" spacing={1} sx={{ py: 6 }}>
                      <Typography sx={{ ...tableBodySx, color: colors.red[200] }}>{errMsg}</Typography>
                      <Button onClick={refresh} sx={{ fontSize: 13, textTransform: "none", color: brandBlue }}>
                        Retry
                      </Button>
                    </Stack>
                  </TableCell>
                </TableRow>
              </TableBody>
            ) : items.length === 0 ? (
              <TableBody>
                <TableRow>
                  <TableCell colSpan={10}>
                    <Stack alignItems="center" spacing={2} sx={{ py: 6 }}>
                      <Box
                        sx={{
                          width: 56,
                          height: 56,
                          borderRadius: "50%",
                          backgroundColor: "#EEF0FE",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <ShieldOutlinedIcon sx={{ fontSize: 28, color: brandBlue }} />
                      </Box>
                      <Typography sx={{ fontSize: 16, fontWeight: 500, color: colors.neutral[1000] }}>
                        Layerd is monitoring
                      </Typography>
                      <Typography sx={{ fontSize: 14, color: colors.neutral[600] }}>
                        There are no events
                      </Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              </TableBody>
            ) : (
              <TableBody>
                {visibleItems.map((f) => {
                  const sev = f.severity || f.rule?.severity;
                  const severityConfig = getSeverityConfig(sev);
                  const statusLabel = getThreatStatusLabel(f.status);
                  const ruleName = f.rule?.name || f.rule_id || "—";
                  const category = f.rule?.category;
                  const matchCount = Number(f.evidence?.match_count || 0);
                  return (
                    <TableRow
                      key={f.id}
                      onClick={() => openDrawer(f)}
                      sx={{
                        cursor: "pointer",
                        "&:hover": { backgroundColor: colors.primary[50] },
                      }}
                    >
                      <TableCell>
                        <Typography sx={tableBodySx}>{f.site_id || "—"}</Typography>
                      </TableCell>
                      <TableCell>
                        <Stack>
                          <Stack direction="row" alignItems="center" spacing={0.75}>
                            <Typography sx={tableBodySx}>{ruleName}</Typography>
                            {/* TI-2 scope=global marker — this rule looks across tenants. */}
                            {f.rule?.scope === "global" && (
                              <Tooltip title="Cross-site rule — runs against every site once per cycle" arrow>
                                <Chip
                                  label="global"
                                  size="small"
                                  sx={{
                                    height: 18,
                                    fontSize: 10,
                                    fontWeight: 600,
                                    background: "#FEECEE",
                                    color: "#A11228",
                                    border: "1px solid #F5D0D4",
                                  }}
                                />
                              </Tooltip>
                            )}
                          </Stack>
                          {f.rule_id ? (
                            <Typography sx={{ fontSize: 11, color: "#1E40AF", lineHeight: "14px" }}>
                              {f.rule_id}
                            </Typography>
                          ) : null}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {/* TI-3: honest subject rendering. */}
                        <SubjectChip
                          kind={f.subject_kind || "unknown"}
                          value={
                            (f.evidence && f.evidence.subject_value) ||
                            (f.evidence && f.evidence.ips && f.evidence.ips[0]) ||
                            null
                          }
                        />
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ ...tableBodySx, fontVariantNumeric: "tabular-nums" }}>
                          {matchCount.toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.25}>
                          <Typography sx={{ ...tableBodySx, fontWeight: 400, color: colors.neutral[1000] }}>
                            {category || "—"}
                          </Typography>
                          {f.first_seen_at ? (
                            <Typography
                              sx={{
                                fontSize: 12,
                                lineHeight: "18px",
                                fontWeight: 400,
                                color: colors.primary[300],
                              }}
                            >
                              First seen {formatDate(f.first_seen_at)}
                            </Typography>
                          ) : null}
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={severityConfig.label}
                          sx={{
                            px: 1,
                            background: severityConfig.bgColor,
                            color: severityConfig.color,
                            borderRadius: "30px",
                            fontSize: 12,
                            fontWeight: "medium",
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Box
                            sx={{
                              width: 12,
                              height: 12,
                              backgroundColor: getThreatStatusColor(f.status),
                              borderRadius: "100%",
                            }}
                          />
                          <Typography sx={tableBodySx}>{statusLabel}</Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        <Typography sx={tableBodySx}>{formatDate(f.last_seen_at) || "—"}</Typography>
                      </TableCell>
                      <TableCell>
                        {f.id ? (
                          <Tooltip title={f.id} arrow placement="top">
                            <Typography
                              sx={{
                                ...tableBodySx,
                                fontFamily: "monospace",
                                color: "#1E40AF",
                                cursor: "default",
                              }}
                            >
                              {f.id.length > 8 ? `${f.id.substring(0, 8)}…` : f.id}
                            </Typography>
                          </Tooltip>
                        ) : (
                          <Typography sx={tableBodySx}>—</Typography>
                        )}
                      </TableCell>
                      <TableCell sx={{ width: 80 }}>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          {/* TI-5: Silence action. Only for open/acknowledged rows —
                              re-suppressing an already-suppressed finding is a no-op UX. */}
                          {(f.status === "open" || f.status === "acknowledged") && (
                            <Tooltip title="Silence this rule for this site" arrow>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  openDrawer(f, { silenceMode: true });
                                }}
                                sx={{
                                  color: colors.neutral[600],
                                  "&:hover": { color: colors.primary[300], backgroundColor: colors.primary[100] },
                                }}
                                aria-label="Silence"
                              >
                                <VolumeOffOutlinedIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Tooltip title="View finding" arrow>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                openDrawer(f);
                              }}
                              sx={{
                                color: colors.neutral[600],
                                p: 0.5,
                                "&:hover": { color: colors.primary[300], backgroundColor: colors.primary[100] },
                              }}
                              aria-label="View finding"
                            >
                              <LayerdIcon icon={DETECTION_ICONS.view} size={18} color="neutral" />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            )}
          </Table>

          {!loading && !errMsg && items.length > 0 && (
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_e, p) => setPage(p)}
              rowsPerPage={DEFAULT_LIMIT}
              rowsPerPageOptions={[DEFAULT_LIMIT]}
              sx={{
                pt: 2,
                mt: 1,
                borderTop: `1px solid ${colors.neutral[200]}`,
                "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                  fontSize: 13,
                  color: colors.neutral[700],
                },
              }}
            />
          )}
        </Box>
      </Stack>

      {/* Drawer */}
      <ThreatInsightDrawer
        open={drawerOpen}
        onClose={closeDrawer}
        finding={selectedFinding}
        initialSilenceMode={drawerSilenceMode}
        onUpdated={() => {
          closeDrawer();
          fetchFindings();
        }}
        onSuccess={(msg) => showSuccess(msg || "Updated")}
        onError={(msg) => showError(msg || "Update failed")}
      />
    </>
  );
}
